import { useQuery } from '@tanstack/react-query';
import { settingsApi } from '@/api/endpoints/settings';
import { useAuthStore } from '@/stores/authStore';
import { getCurrencySymbol } from '@/utils/format';

/**
 * Hook that returns jurisdiction defaults for the active company's country.
 *
 * Usage:
 *   const { jurisdiction, isLoading, isReady, statutoryFields } = useJurisdiction();
 *   if (statutoryFields.includes('paye')) { // show PAYE column }
 */
export function useJurisdiction() {
  const activeCompany = useAuthStore((s) => s.getActiveCompany());
  const countryCode = activeCompany?.country_code;

  const { data: jurisdiction, isLoading } = useQuery({
    queryKey: ['jurisdiction-defaults', countryCode],
    queryFn: () => settingsApi.getJurisdictionDefaults(countryCode!).then((res) => res.data),
    staleTime: 5 * 60 * 1000, // 5 minutes — jurisdiction rarely changes
    enabled: !!countryCode,
  });

  // Helper: list of statutory deduction codes (e.g., ['paye', 'pension_ee', 'nhf', 'nsitf', 'itf'])
  const statutoryFields = (jurisdiction?.statutory_deductions ?? []).map((d: any) => d.code);

  // Helper: check if a specific statutory field should be shown
  const hasStatutoryField = (code: string) => statutoryFields.includes(code);

  // Currency from jurisdiction plugin
  const currencyCode = jurisdiction?.currency_code || '';
  const currencySymbol = currencyCode ? getCurrencySymbol(currencyCode) : '';

  // Tax rate defaults (from DB overrides or jurisdiction plugin fallback)
  const vatRate = jurisdiction?.vat_rate ?? 0;
  const whtRateProfessional = jurisdiction?.wht_rate_professional ?? 0;
  const whtRateDirector = jurisdiction?.wht_rate_director ?? 0;

  return {
    jurisdiction,
    isLoading,
    isReady: !!jurisdiction && !!countryCode,
    countryCode: countryCode || '',
    statutoryFields,
    hasStatutoryField,
    currencySymbol,
    currencyCode,
    vatRate,
    whtRateProfessional,
    whtRateDirector,
  };
}
